/**
 * Parse WalletConnect / Hedera SDK errors into user-friendly messages.
 */

const SAFE_BACKEND_MESSAGE = /^[\w\s.,!?'"()-]{1,200}$/;

const REJECTION = /user[ _]?reject|rejected by user|user denied|user cancel/i;

/** Whether the person declined the request in their wallet. */
export const isWalletRejection = (error: unknown): boolean =>
  REJECTION.test(String((error as any)?.message || error || ''));

export const getWalletErrorMessage = (error: unknown, fallback: string): string => {
  const errStr = String((error as any)?.message || error || '');

  // From services/walletExecution: already worded for the person
  if (['NotExecutedError', 'UnconfirmedError'].includes((error as any)?.name)) {
    return errStr;
  }
  if (isWalletRejection(error)) {
    return 'Transaction was rejected in your wallet';
  }
  if (/insufficient.*balance|INSUFFICIENT_PAYER_BALANCE/i.test(errStr)) {
    return 'Insufficient balance to cover the transaction fee';
  }
  if (/INVALID_SIGNATURE/i.test(errStr)) {
    return 'Invalid signature — your wallet key may not match this account';
  }
  if (/TRANSACTION_EXPIRED/i.test(errStr)) {
    return 'Transaction expired — please try again';
  }
  if (/BUSY/i.test(errStr)) {
    return 'Hedera network is busy — please try again shortly';
  }
  if (/SCHEDULE_ALREADY_EXECUTED/i.test(errStr)) {
    return 'This transaction has already been executed';
  }
  // WalletConnect's 5-minute timeout, for wallet requests that
  // services/walletExecution doesn't cover (signing without submitting).
  // Don't invite a blind retry.
  if (/Request expired/i.test(errStr)) {
    return "Your wallet didn't respond in time. Check your wallet and this wallet's history before trying again";
  }
  if (/timeout|timed out/i.test(errStr)) {
    return 'Wallet did not respond in time — please try again';
  }
  if (/circuit.?breaker|temporarily unavailable/i.test(errStr)) {
    return errStr; // Already user-friendly from circuit breaker
  }
  if (/Retrying.*attempt/i.test(errStr)) {
    return errStr; // Retry progress messages
  }
  if (/UNAVAILABLE|connection refused|ECONNREFUSED/i.test(errStr)) {
    return 'Cannot connect to the network. Check if the network is online.';
  }
  if (/DEADLINE_EXCEEDED/i.test(errStr)) {
    return 'Network request timed out. The network may be slow or unreachable.';
  }

  // Only forward backend messages that look like safe, human-readable text
  const backendMsg = (error as any)?.response?.data?.message;
  if (typeof backendMsg === 'string' && SAFE_BACKEND_MESSAGE.test(backendMsg)) {
    return backendMsg;
  }

  return fallback;
};
